import * as THREE from 'three';

/**
 * Panel'in kırmızı ön kenar çizgisinin direction'ını hesaplar
 * @param {THREE.Object3D} panel - Panel object
 * @returns {THREE.Vector3} - Kırmızı çizginin direction vektörü (normalize edilmiş)
 */
export function getPanelFrontDirection(panel) {
  if (!panel) return new THREE.Vector3(1, 0, 0);
  
  const panelRotation = panel.rotation.z;
  const symmetricalOffset = panel.userData?.symmetricalOffset || 0;
  const dir = panel.userData?.dir || 1;
  
  // Panel'in kendi koordinat sistemindeki sağ yön (ön kenar yönü)
  const panelRightDir = new THREE.Vector3(1, 0, 0);
  
  // Panel rotasyonunu uygula
  panelRightDir.applyAxisAngle(new THREE.Vector3(0, 0, 1), panelRotation);
  
  return panelRightDir.normalize();
}

/**
 * Panel ön kenar çizgisine dik yönü hesaplar
 * @param {THREE.Object3D} panel - Panel object
 * @returns {THREE.Vector3} - Dik direction vektörü (normalize edilmiş)
 */
export function getPanelPerpendicularDirection(panel) {
  if (!panel) return new THREE.Vector3(1, 0, 0);
  
  const panelRotation = panel.rotation.z;
  const dir = panel.userData?.dir || 1;
  
  // Kırmızı çizgi dikey (Y yönünde), panel'in ön yönü ise yatay (X yönünde)
  // Panel'in ön yönü (kırmızı çizgiye dik olan yön)
  const panelFrontDir = new THREE.Vector3(dir, 0, 0); // Panel'in X yönü (ön/arka)
  
  // Panel rotasyonunu uygula
  panelFrontDir.applyAxisAngle(new THREE.Vector3(0, 0, 1), panelRotation);
  
  return panelFrontDir.normalize();
}

/**
 * Panel çıkış noktasından dik yönde çıkış hesaplar
 * @param {THREE.Vector3} exitPoint - Panel çıkış noktası (yeşil daire pozisyonu)
 * @param {THREE.Vector3} mousePosition - Mouse pozisyonu
 * @param {THREE.Object3D} panel - Panel object
 * @returns {THREE.Vector3} - Dik çıkış noktası
 */
export function calculatePerpendicularExitPoint(exitPoint, mousePosition, panel) {
  const perpDir = getPanelPerpendicularDirection(panel);
  
  // Mouse ile çıkış noktası arasındaki vektör
  const mouseVector = mousePosition.clone().sub(exitPoint);
  
  // Mouse pozisyonunun dik yöndeki projeksiyon mesafesini hesapla
  const projectedDistance = mouseVector.dot(perpDir);
  
  // Negatif mesafe varsa minimum 10 birim uzaklığa ayarla
  const finalDistance = Math.max(Math.abs(projectedDistance), 10) * Math.sign(projectedDistance);
  
  // Çıkış noktasından dik yönde çık
  return exitPoint.clone().add(perpDir.clone().multiplyScalar(finalDistance));
}

/**
 * Panel ön kenar çizgisi üzerindeki çıkış pozisyonunu bulur
 * @param {THREE.Object3D} panel - Panel object
 * @param {THREE.Vector3} targetPoint - Hedef nokta
 * @returns {THREE.Vector3} - Kırmızı çizgi üzerindeki en yakın nokta
 */
export function getClosestPointOnFrontEdge(panel, targetPoint) {
  if (!panel) return targetPoint.clone();
  
  const panelPosition = panel.position;
  const panelRotation = panel.rotation.z;
  const symmetricalOffset = panel.userData?.symmetricalOffset || 0;
  const dir = panel.userData?.dir || 1;
  const height = panel.userData?.height || 25;
  
  const halfW = 7.5; // Panel genişliği/2
  const halfH = height / 2;
  
  // Panel'in koordinat sistemi yönleri
  const panelRightDir = new THREE.Vector3(1, 0, 0);
  const panelUpDir = new THREE.Vector3(0, 1, 0);
  
  // Rotasyonu uygula
  panelRightDir.applyAxisAngle(new THREE.Vector3(0, 0, 1), panelRotation);
  panelUpDir.applyAxisAngle(new THREE.Vector3(0, 0, 1), panelRotation);
  
  // Kırmızı çizginin başlangıç ve bitiş noktaları (world koordinatlarında)
  const frontLineX = halfW + symmetricalOffset * dir;
  const frontStartPoint = panelPosition.clone()
    .add(panelRightDir.clone().multiplyScalar(frontLineX))
    .add(panelUpDir.clone().multiplyScalar(-halfH));
  const frontEndPoint = panelPosition.clone()
    .add(panelRightDir.clone().multiplyScalar(frontLineX))
    .add(panelUpDir.clone().multiplyScalar(halfH));
  
  // Çizgi üzerindeki en yakın noktayı bul
  const lineDir = frontEndPoint.clone().sub(frontStartPoint).normalize();
  const toTarget = targetPoint.clone().sub(frontStartPoint);
  const projectedLength = toTarget.dot(lineDir);
  
  // Çizgi sınırları içinde tut
  const clampedLength = Math.max(0, Math.min(projectedLength, frontEndPoint.distanceTo(frontStartPoint)));
  
  return frontStartPoint.clone().add(lineDir.multiplyScalar(clampedLength));
}